import React, { useMemo, useState } from 'react';
import { Accordion, AccordionContent, AccordionTitle, Icon, Label, Table } from 'semantic-ui-react';
import '../../css/FutureReadiness.css';
import { RoadblocksTable } from './RoadblocksTable';
import { DailyReadinessGrid } from './DailyReadinessGrid';

// ─── Assets out of service panel ─────────────────────────────────────────────────────────────────

const AssetsPanel = ({ days }) => {
    const [open, setOpen] = useState(true);

    const allAssets = useMemo(() => {
        const seen = new Set();
        const result = [];
        for (const day of (days || [])) {
            for (const asset of (day.assetsOutOfService || [])) {
                if (!seen.has(asset.problemId)) {
                    seen.add(asset.problemId);
                    result.push(asset);
                }
            }
        }
        return result;
    }, [days]);

    const hasAssets = allAssets.length > 0;

    return (
        <Accordion
            fluid
            styled
            className={`fr-assets-accordion ${hasAssets ? 'fr-assets-accordion--issue' : 'fr-assets-accordion--ok'}`}
        >
            <AccordionTitle
                active={open}
                onClick={() => setOpen(prev => !prev)}
                className={hasAssets ? 'fr-assets-title--issue' : 'fr-assets-title--ok'}
            >
                <Icon name="dropdown" />
                <Icon name="wrench" color={hasAssets ? 'red' : 'green'} />
                Assets Out of Service&nbsp;
                {hasAssets
                    ? <Label color="red" circular size="tiny" aria-label={`${allAssets.length} assets out of service`}>{allAssets.length}</Label>
                    : <Label color="green" circular size="tiny" aria-label="No assets out of service">0</Label>
                }
            </AccordionTitle>
            <AccordionContent active={open}>
                {hasAssets ? (
                    <div className="fr-assets-scroll">
                        <Table basic="very" compact unstackable className="fr-assets-table">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell scope="col">Asset Name</Table.HeaderCell>
                                    <Table.HeaderCell scope="col">Type</Table.HeaderCell>
                                    <Table.HeaderCell scope="col">Summary</Table.HeaderCell>
                                    <Table.HeaderCell scope="col">Est. Fix Date</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {allAssets.map(a => (
                                    <Table.Row key={a.problemId}>
                                        <Table.Cell><strong>{a.iccName}</strong></Table.Cell>
                                        <Table.Cell>{a.assetType}</Table.Cell>
                                        <Table.Cell>{a.summary}</Table.Cell>
                                        <Table.Cell>{a.estimatedFixDate || 'Unknown'}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                ) : (
                    <div className="fr-assets-operational">
                        <Icon name="check circle" color="green" />
                        All assets are operational during this period.
                    </div>
                )}
            </AccordionContent>
        </Accordion>
    );
};

// ─── TeamReadinessSection ─────────────────────────────────────────────────────────────────

export const TeamReadinessSection = ({ team, defaultOpen }) => {
    const [open, setOpen] = useState(defaultOpen ?? true);
    const roadblockCount = team.openRoadblocks?.length ?? 0;

    return (
        <div className="fr-team-section">
            {/* Header */}
            <div
                role="button"
                tabIndex={0}
                onClick={() => setOpen(prev => !prev)}
                onKeyDown={(e) => e.key === 'Enter' && setOpen(prev => !prev)}
                aria-expanded={open}
                className={`fr-team-header${open ? ' fr-team-header--open' : ''}`}
            >
                <Icon
                    name={open ? 'angle down' : 'angle right'}
                    className="fr-team-toggle-icon"
                />
                <span className="fr-team-name">{team.name}</span>
                {team.market && (
                    <span className="fr-team-market">{team.market}</span>
                )}
                {roadblockCount > 0 && (
                    <Label color="orange" size="tiny" circular>
                        {roadblockCount} open roadblock{roadblockCount !== 1 ? 's' : ''}
                    </Label>
                )}
            </div>

            {/* Body */}
            {open && (
                <div className="fr-team-body">
                    <RoadblocksTable roadblocks={team.openRoadblocks} />
                    <DailyReadinessGrid days={team.days} />
                    <AssetsPanel days={team.days} />
                </div>
            )}
        </div>
    );
};
